import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, DeadlineChip } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

function ResolveComplaintInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const { id } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState(3);
  const [extendReason, setExtendReason] = useState('');
  const [extending, setExtending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [lightbox, setLightbox] = useState(null);
  const [acceptDays, setAcceptDays] = useState(7);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => { fetchComplaint(); }, [id]);

  const fetchComplaint = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/authority/assigned-complaint/${id}`);
      setComplaint(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaint');
      if (e.response?.status === 404) setTimeout(() => navigate('/authority/assigned'), 2500);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      await api.post(`/authority/resolve/${id}`, { resolution_notes: resolutionNotes });
      setSuccess('Complaint resolved successfully!');
      setTimeout(() => navigate('/authority/assigned'), 2000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleAcceptEscalated = async () => {
    setActing(true);
    try {
      await api.post(`/authority/resolve/${id}`, { action: 'accept', deadline_days: acceptDays });
      setSuccess(`Complaint accepted. You have ${acceptDays} days to resolve it.`);
      fetchComplaint();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept');
    } finally {
      setActing(false);
    }
  };

  const handleRejectEscalated = async () => {
    if (!rejectReason.trim()) { setError('Rejection reason is required'); return; }
    setActing(true);
    try {
      await api.post(`/authority/resolve/${id}`, { action: 'reject', rejection_reason: rejectReason });
      setSuccess('Complaint rejected.');
      setTimeout(() => navigate('/authority/assigned'), 2000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to reject');
    } finally {
      setActing(false);
    }
  };

  const handleExtend = async () => {
    if (!extendReason.trim()) { setError('Reason is required'); return; }
    setExtending(true);
    try {
      await api.post(`/authority/extend-deadline/${id}`, { extra_days: extendDays, reason: extendReason });
      setShowExtendModal(false);
      setSuccess(`Deadline extended by ${extendDays} days`);
      fetchComplaint();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to extend deadline');
    } finally {
      setExtending(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString() : 'N/A';

  const metaBoxStyle = {
    padding: '10px 14px', borderRadius: 9, marginBottom: 8,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Resolve Complaint"
            subtitle="Review and take action"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/authority/assigned')}>Back</button>
            }
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            {success && <div style={s.alertOk}>{success}</div>}

            {complaint && (
              <PageHeader
                icon="✅"
                title="Resolve Complaint"
                subtitle={complaint.title}
                stats={[
                  { label: 'Status', value: complaint.status },
                  { label: 'Upvotes', value: complaint.upvotes || 0 },
                ]}
              />
            )}

            {!complaint ? (
              <div style={s.alertErr}>Complaint not found or not assigned to you.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
                {/* Left: Details */}
                <div>
                  <div style={{ ...s.card, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', flex: 1, marginRight: 12 }}>{complaint.title}</div>
                      <DeadlineChip seconds={complaint.time_left_seconds} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
                      {[
                        ['Student', complaint.student_name],
                        ['Email', complaint.student_email],
                        [complaint.department ? 'Department' : 'Category', complaint.department || complaint.university_category || '—'],
                        ['Verified By', complaint.verified_by_name],
                      ].map(([label, value]) => (
                        <div key={label} style={metaBoxStyle}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>{label}</div>
                          <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ ...s.sectionTitle, marginBottom: 8 }}>Description</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{complaint.description}</div>
                  </div>

                  {/* Extension History */}
                  {complaint.extension_count > 0 && (
                    <div style={{
                      ...s.card, marginBottom: 16,
                      background: 'rgba(245,158,11,0.06)',
                      border: `1px solid ${COLORS.amber}44`,
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.amber, marginBottom: 10 }}>
                        Extended {complaint.extension_count} time{complaint.extension_count > 1 ? 's' : ''}
                      </div>
                      {complaint.last_extension_reason && (
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontStyle: 'italic', borderLeft: `3px solid ${COLORS.amber}`, paddingLeft: 12 }}>
                          "{complaint.last_extension_reason}"
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
                            — {complaint.last_extension_by_name}, {fmt(complaint.last_extension_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timeline */}
                  <div style={s.card}>
                    <div style={{ ...s.sectionTitle, marginBottom: 14 }}>Timeline</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[
                        ['Created', complaint.created_at],
                        [`Verified by ${complaint.verified_by_name}`, complaint.verified_at],
                        ['Resolution Deadline', complaint.resolution_deadline],
                      ].filter(([, d]) => d).map(([label, date]) => (
                        <div key={label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.cyan, marginTop: 4, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{label}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{fmt(date)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Images */}
                  {complaint.images?.length > 0 && (
                    <div style={{ ...s.card, marginTop: 16 }}>
                      <div style={{ ...s.sectionTitle, marginBottom: 12 }}>Attachments ({complaint.images.length})</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(90px,1fr))', gap: 10 }}>
                        {complaint.images.map((img, i) => {
                          const url = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/uploads/complaints/${img}`;
                          const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(img);
                          return isVideo ? (
                            <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox({ url, isVideo: true })}>
                              <video src={url} muted style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', display: 'block' }} />
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', borderRadius: 8 }}>
                                <span style={{ fontSize: 22 }}>▶</span>
                              </div>
                            </div>
                          ) : (
                            <img key={i} src={url} alt=""
                              onClick={() => setLightbox({ url, isVideo: false })}
                              style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', transition: 'transform 0.15s' }}
                              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
                              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Lightbox */}
                  {lightbox && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setLightbox(null)}>
                      <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 20, right: 28, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }}>✕</button>
                      {lightbox.isVideo
                        ? <video src={lightbox.url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10 }} onClick={e => e.stopPropagation()} />
                        : <img src={lightbox.url} alt="Full size" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10, objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
                      }
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Escalated — must accept or reject */}
                  {complaint.status === 'escalated' ? (
                    <>
                      <div style={{ ...s.card, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#fca5a5', marginBottom: 4 }}>⚠️ Escalated Complaint</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 14 }}>
                          This complaint was auto-escalated to you. You must accept it (and set a deadline) or reject it.
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <label style={s.label}>Resolution Deadline (days)</label>
                          <select value={acceptDays} onChange={e => setAcceptDays(parseInt(e.target.value))} style={s.select}>
                            {[3,5,7,10,14,21,30].map(d => <option key={d} value={d}>{d} days</option>)}
                          </select>
                        </div>
                        <button className="ur-btn-hover"
                          style={{ ...s.btnSuccess, width: '100%', justifyContent: 'center', display: 'flex', marginBottom: 10, opacity: acting ? 0.7 : 1 }}
                          onClick={handleAcceptEscalated} disabled={acting}>
                          {acting ? 'Processing...' : '✓ Accept & Set Deadline'}
                        </button>

                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12, marginTop: 4 }}>
                          <label style={s.label}>Rejection Reason</label>
                          <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            placeholder="Why is this complaint being rejected?..."
                            style={{ ...s.textarea, marginBottom: 10 }} />
                          <button className="ur-btn-hover"
                            style={{ ...s.btnDanger || { ...s.btnPrimary, background: 'linear-gradient(135deg,#ef4444,#dc2626)' },
                              width: '100%', justifyContent: 'center', display: 'flex', opacity: (acting || !rejectReason.trim()) ? 0.6 : 1 }}
                            onClick={handleRejectEscalated} disabled={acting || !rejectReason.trim()}>
                            {acting ? 'Processing...' : '✕ Reject Complaint'}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Resolve */}
                      <div style={s.card}>
                        <div style={{ ...s.sectionTitle, marginBottom: 12 }}>Mark as Resolved</div>
                        <textarea
                          rows={4}
                          placeholder="Resolution notes (optional)..."
                          value={resolutionNotes}
                          onChange={e => setResolutionNotes(e.target.value)}
                          style={{ ...s.textarea, marginBottom: 12 }}
                        />
                        <button
                          className="ur-btn-hover"
                          style={{ ...s.btnSuccess, width: '100%', justifyContent: 'center', display: 'flex', opacity: resolving ? 0.7 : 1 }}
                          onClick={handleResolve}
                          disabled={resolving}
                        >
                          {resolving ? 'Processing...' : 'Confirm Resolution'}
                        </button>
                      </div>

                      {/* Extend */}
                      <div style={s.card}>
                        <div style={{ ...s.sectionTitle, marginBottom: 8 }}>Extend Deadline</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>Need more time to resolve this complaint?</div>
                        <button
                          className="ur-btn-hover"
                          style={{ ...s.btnPrimary, width: '100%', justifyContent: 'center', display: 'flex',
                            background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}
                          onClick={() => setShowExtendModal(true)}
                        >
                          Request Extension
                        </button>
                      </div>
                    </>
                  )}

                  {/* Info */}
                  <div style={s.card}>
                    <div style={{ ...s.sectionTitle, marginBottom: 12 }}>Complaint Info</div>
                    {[
                      ['ID', `#${complaint.id}`],
                      ['Status', complaint.status],
                      ['Upvotes', complaint.upvotes || 0],
                      complaint.extension_count > 0 && ['Extensions', complaint.extension_count],
                    ].filter(Boolean).map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                        <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Extend Modal */}
      {showExtendModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)',
        }} onClick={() => setShowExtendModal(false)}>
          <div style={{
            ...s.card, maxWidth: 480, width: '90%', animation: 'scaleIn 0.25s ease-out',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ ...s.sectionTitle }}>Extend Resolution Deadline</div>
              <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 22, cursor: 'pointer' }} onClick={() => setShowExtendModal(false)}>×</button>
            </div>

            <div style={{ ...metaBoxStyle, marginBottom: 16, borderColor: `${COLORS.amber}44` }}>
              <div style={{ fontSize: 11, color: COLORS.amber, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Current Deadline</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{fmt(complaint?.resolution_deadline)}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={s.label}>Additional Days</label>
              <select value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value))} style={s.select}>
                {[1,2,3,5,7,10,14].map(d => <option key={d} value={d}>{d} Day{d > 1 ? 's' : ''}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={s.label}>Reason (required)</label>
              <textarea rows={3} value={extendReason} onChange={e => setExtendReason(e.target.value)}
                placeholder="Explain why more time is needed..."
                style={s.textarea} />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>This reason will be visible to the student and admin</div>
            </div>

            <button
              className="ur-btn-hover"
              style={{ ...s.btnPrimary, width: '100%', justifyContent: 'center', display: 'flex',
                opacity: (!extendReason.trim() || extending) ? 0.6 : 1,
                cursor: (!extendReason.trim() || extending) ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg,#f59e0b,#d97706)',
              }}
              onClick={handleExtend}
              disabled={!extendReason.trim() || extending}
            >
              {extending ? 'Processing...' : 'Confirm Extension'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResolveComplaint() {
  return (
    <ThemeProvider>
      <ResolveComplaintInner />
    </ThemeProvider>
  );
}
